import React, { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Box, TextField, Button, Alert, CircularProgress, Link, Typography } from '@mui/material'
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import LockResetIcon from '@mui/icons-material/LockReset'
import { useTranslation } from 'react-i18next'
import { authService } from '@/services/authService'
import AuthShell from '@/components/AuthShell'
import { resolveApiErrorMessage } from '@/utils/apiErrorMessage'
import type { SendResetLinkCredentials } from '@/types/auth'

export default function SendResetLinkPage() {
  const [credentials, setCredentials] = useState<SendResetLinkCredentials>({ login: '' })
  const [error, setError] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const navigate = useNavigate()
  const { t } = useTranslation('auth')
  const { t: tErrors } = useTranslation('errors')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setCredentials((prev) => ({ ...prev, [name]: value }))
    if (error) setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')
    try {
      await authService.sendResetLink(credentials)
      setSuccess(true)
    } catch (err) {
      setError(resolveApiErrorMessage(err, tErrors))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (success) {
    return (
      <AuthShell
        title={t('resetLinkSentTitle')}
        subtitle={t('resetLinkSentBody')}
        icon={<MarkEmailReadOutlinedIcon />}
        iconTone="success"
      >
        <Button fullWidth variant="contained" size="large" onClick={() => navigate('/login')} sx={{ py: 1.25 }}>
          {t('backToLogin')}
        </Button>
      </AuthShell>
    )
  }

  return (
    <AuthShell title={t('resetPasswordTitle')} subtitle={t('resetPasswordSubtitle')} icon={<LockResetIcon />}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="login"
          label={t('emailAddress')}
          name="login"
          autoComplete="email"
          autoFocus
          value={credentials.login}
          onChange={handleChange}
          disabled={isSubmitting}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          sx={{ mt: 2.5, mb: 2, py: 1.25 }}
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t('sendResetLink')}
        </Button>
        <Typography variant="body2" align="center" color="text.secondary">
          {t('rememberPassword')}{' '}
          <Link component={RouterLink} to="/login" underline="hover" fontWeight={600}>
            {t('signInHere')}
          </Link>
        </Typography>
      </Box>
    </AuthShell>
  )
}
